import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { callZigzagFunction, compareWithAppVersion, isBrowser, isIOS, toQueryString } from '@croquiscom/web2app';
import { toast, useBottomSheet } from '@croquiscom-pvt/zds';
import copy from 'copy-to-clipboard';
import { isNull } from 'lodash';
import { useRouter } from 'next/router';

import { ZigzagEvent } from '@common/app-manager';
import { getConfig } from '@common/config';
import { KillSwitchKey, useKillSwitchService } from '@common/kill-switch';
import { getKakaoAPI } from '@common/utils';
import {
  isPreview,
  PdpBrowsingType,
  PdpProductEntryType,
  useCatalogProductIdParams,
  useIsAuthenticated,
  usePdpBaseInfo,
  usePdpConfig,
  usePdpWebEvents,
  useProductThumbnail,
} from '@domains/pdp-common';

import { createShortUrlInAppsflyer } from '../graphql/create-short-url-in-appsflyer.2';
import { useAffiliateShareDeepLink } from '../hooks/useAffiliateShareDeepLink';
import { useAirBridgeProductShareEvents } from '../log/useAirBridgeProductShareEvents';
import { useProductShareUBL } from '../log/useProductShareUBL';

const config = getConfig();

interface ShareArgs {
  shopId: string;
  catalogProductId: string;
  goodsName: string;
  goodsImageUrl: string;
  deeplinkUrl: string;
  isAffiliateAvailable?: boolean;
}

function generateProductUrl(id: string) {
  try {
    return `${config.appUrl}/catalog/products/${id}`;
  } catch {
    return;
  }
}

export const useProductShareService = () => {
  const { open: openBottomSheet, close: closeBottomSheet } = useBottomSheet('custom');
  const { clickShareUBL, shareClickUBL, clickCopyAffiliateUrlUBL, clickCopyUrlUBL, clickShareKakaoUBL } =
    useProductShareUBL();
  const param = useCatalogProductIdParams();
  const { product, brand_home, shop } = usePdpBaseInfo(param);
  const seller_home = brand_home ?? shop;
  const { asPath } = useRouter();
  const isPreviewPath = isPreview(asPath);

  const { onAirBridgeShare } = useAirBridgeProductShareEvents({
    catalogProduct: product,
  });

  const { is_enabled: is_kakao_share_feature_enabled } = useKillSwitchService(KillSwitchKey.SHOULD_SHOW_KAKAO_SHARE);

  const { url } = useProductThumbnail();
  const { isZigzagApp } = usePdpConfig();
  const [os, setOs] = useState<'ios' | 'android' | null>(null);
  const [urlForShare, setUrlForShare] = useState<string | null>(null);
  const [isShareSheetActive, setIsShareSheetActive] = useState(false);
  const { shareEvent } = usePdpWebEvents();
  const { isLoggedIn } = useIsAuthenticated();
  const { is_activated_affiliate, isAffiliateStatusLoading, createAffiliateShareDeepLink } = useAffiliateShareDeepLink({
    enabled: isLoggedIn,
  });
  const affiliateStatusRef = useRef({ is_activated_affiliate, isAffiliateStatusLoading });
  affiliateStatusRef.current = { is_activated_affiliate, isAffiliateStatusLoading };

  const product_url = generateProductUrl(product.id) ?? url;
  const getPDPUrl = useCallback(
    () =>
      `zigzag://open/product_detail?${toQueryString({
        browsing_type:
          product.entry_type !== PdpProductEntryType.DIRECT && compareWithAppVersion('7.0.0')
            ? PdpBrowsingType.Z_BROWSER
            : PdpBrowsingType.INTERNAL_BROWSER,
        url: product_url,
        shop_id: seller_home.id,
        shop_product_no: null,
        catalog_product_id: product.id,
      })}`,
    [product.entry_type, product.id, product_url, seller_home.id],
  );

  const openShareSheet = (children: ReactNode) => {
    if (!isZigzagApp) {
      shareClickUBL(product.id);
    }

    clickShareUBL();

    if (!compareWithAppVersion('6.52.0')) {
      setIsShareSheetActive(true);
      openBottomSheet({ children });
      getKakaoAPI();
      return;
    }

    const args: ShareArgs = {
      shopId: seller_home.id,
      catalogProductId: product.id,
      goodsName: product.name,
      goodsImageUrl: url,
      deeplinkUrl: getPDPUrl(),
    };

    const isDev = window.location.origin.includes('alpha') || window.location.origin.includes('dev');
    if (isDev || compareWithAppVersion('7.57.0')) {
      args.isAffiliateAvailable = true;
    }
    callZigzagFunction('share')(args);
  };

  const copyShortUrl = async () => {
    // 어필리에이트 상태 조회 중이면 대기 안내
    if (affiliateStatusRef.current.isAffiliateStatusLoading) {
      toast({ content: '공유링크를 생성하고 있어요.\n잠시만 기다려 주세요.' });
      return;
    }

    // 공유리워드 사용자의 경우, deep_link만 이용해 공유 링크를 생성 합니다.
    if (affiliateStatusRef.current.is_activated_affiliate) {
      clickCopyAffiliateUrlUBL();
      return createAffiliateShareDeepLink(
        {
          product_id: product.id,
          shop_id: seller_home.id,
        },
        {
          onSuccess: (data) => {
            if (data?.short_url) {
              copy(data.short_url, { message: '링크를 복사해주세요.' });
              toast({ content: '🔗 공유 리워드용 링크를 복사했어요' });
              setIsShareSheetActive(false);
              closeBottomSheet();
            }
          },
        },
      );
    }

    // 웹 환경에서 urlForShare가 아직 없으면 product_url 사용
    const urlToCopy = urlForShare || product_url;

    if (!urlToCopy) {
      return;
    }

    clickCopyUrlUBL();
    copy(urlToCopy, { message: '링크를 복사해주세요.' });
    onAirBridgeShare('copy_link');
    toast({ content: '🔗 링크를 복사했어요' });
    setIsShareSheetActive(false);
    closeBottomSheet();
    if (!isPreviewPath) {
      shareEvent({
        method: 'link',
        content_type: 'catalog_product',
        product_id: product.id,
      });
    }
  };

  const shareWithKakao = async () => {
    const Kakao = (window as any).Kakao;

    let sendFunc = () => {
      Kakao.Share.sendScrap({
        requestUrl: product_url,
      });
    };
    if (isZigzagApp) {
      const isAvailable = await ZigzagEvent.ckla();
      if (!isAvailable) {
        toast({ content: '카카오톡이 설치되어 있지 않아 해당 상품을 공유할 수 없습니다.' });
        return;
      }
      sendFunc = () => {
        Kakao.Share.sendCustom({
          templateId: 41434,
          templateArgs: {
            shop_main_domain: seller_home.main_domain,
            shop_name: seller_home.name,
            shop_image: seller_home.typical_image_url,
            product_name: product.name,
            image_url: url,
            price: product.product_price.final_discount_info.discount_price,
            discount_rate: product.product_price.final_discount_info.discount_rate,
            deep_link_url: encodeURIComponent(
              `/product_detail?${toQueryString({
                browsing_type: PdpBrowsingType.INTERNAL_BROWSER,
                url: product_url,
                shop_id: seller_home.id,
                shop_product_no: product.external_code,
                catalog_product_id: product.id,
              })}`,
            ),
          },
        });
      };
    }

    try {
      clickShareKakaoUBL();
      sendFunc();
      onAirBridgeShare('kakao');
      setIsShareSheetActive(false);
      closeBottomSheet();
    } catch (error) {
      toast({ content: '카카오톡 공유하기에 실패하였습니다.' });
    }

    if (!isPreviewPath) {
      shareEvent({
        method: 'kakao',
        content_type: 'catalog_product',
        product_id: product.id,
      });
    }
  };

  useEffect(() => {
    setOs(isIOS(navigator.userAgent) ? 'ios' : 'android');
  }, []);

  useEffect(() => {
    if (!isShareSheetActive || (urlForShare && urlForShare.length > 0)) {
      return;
    }
    const buildShortUrl = async () => {
      const url = getPDPUrl();
      const { data } = await createShortUrlInAppsflyer({ deep_link: url });
      setUrlForShare(data.createShortUrlInAppsflyer?.short_url ?? '');
    };
    if (isBrowser() && isZigzagApp) {
      buildShortUrl();
      return;
    }
    setUrlForShare(product_url);
  }, [getPDPUrl, isShareSheetActive, isZigzagApp, urlForShare]);

  return {
    isIOS: isNull(os) ? null : os === 'ios',
    isPreviewPath,
    isShareSheetActive,
    is_kakao_share_feature_enabled,
    setIsShareSheetActive,
    openShareSheet,
    copyShortUrl,
    shareWithKakao,
  };
};
